import { useMutation, useQuery } from '@tanstack/react-query';
import {
  checkAuth,
  sendResetCode,
  login,
  resetPassword,
  signup,
  verifyResetCode,
  verifyEmail,
  resendVerification,
  logout,
  socialCallback,
  sendEmailVerificationOtp,
  verifyInvitedUserEmail,
  socialProviderInvitationCallbackUrl,
  signupInvitedUser,
  getAccountInvitation,
  getInvitationById,
} from './serverActions';

import { createMutation } from '../shared/createMutation';
import { createServerAction } from '../shared/createServerAction';
import { InvitationParams, VerifiedUserResponse } from './types';
import { InvitationGetter } from '../setting/types';
import { apiFetch } from '../shared/apiFetch';
import { saveUserSession } from './utils';

export const useCheckAuth = () => {
  return useMutation({
    mutationFn: createMutation(checkAuth),
  });
};
export const useLogin = () => {
  return useMutation({
    mutationFn: createMutation(login),
  });
};
export const useSocialCallback = () => {
  return useMutation({
    mutationFn: createMutation(socialCallback),
  });
};
export const useSignUp = () => {
  return useMutation({
    mutationFn: createMutation(signup),
  });
};
export const useVerifyEmail = () => {
  return useMutation({
    mutationFn: createMutation(verifyEmail),
  });
};
export const useResendVerification = () => {
  return useMutation({
    mutationFn: createMutation(resendVerification),
  });
};
export const useSendResetCode = () => {
  return useMutation({
    mutationFn: createMutation(sendResetCode),
  });
};
export const useVerifyResetCode = () => {
  return useMutation({
    mutationFn: createMutation(verifyResetCode),
  });
};
export const useResetPassword = () => {
  return useMutation({
    mutationFn: createMutation(resetPassword),
  });
};
export const useLogout = () => {
  return useMutation({
    mutationFn: createMutation(logout),
  });
};
// ************************ Send Invitation Email Verification OTP **************************
export const useSendEmailVerificationOtp = () => {
  return useMutation({
    mutationKey: ['send-email-verification-otp'],
    mutationFn: createMutation(sendEmailVerificationOtp),
  });
};
// ************************ Verify Invited User Email **************************
export const useVerifyInvitedUserEmail = () => {
  return useMutation({
    mutationKey: ['verify-invited-user-email'],
    mutationFn: createMutation(verifyInvitedUserEmail),
  });
};
// ************************ Social Provider Invitation Callback **************************
export const useSocialProviderInvitationCallbackUrl = () => {
  return useMutation({
    mutationKey: ['social-provider-invitation-callback'],
    mutationFn: createMutation(socialProviderInvitationCallbackUrl),
  });
};
// ************************ Signup Invited User **************************
export const useSignupInvitedUser = () => {
  return useMutation({
    mutationKey: ['signup-invited-user'],
    mutationFn: createMutation(signupInvitedUser),
  });
};
// ************************ Get Account Invitation **************************
export const useGetAccountInvitation = ({
  enabled,
  ...params
}: InvitationParams & { enabled: boolean }) => {
  return useQuery({
    queryKey: ['get-account-invitation', params.id, params.uuid],
    queryFn: createMutation(async () => getAccountInvitation(params)),
    enabled,
  });
};
// ************************ Get Invitation By ID **************************
export const useGetInvitationById = (id: string) => {
  return useQuery({
    queryKey: ['get-account-invitation', id],
    queryFn: createMutation(
      createServerAction(async () => getInvitationById(id))
    ),
  });
};
// ************************ Join Account By Invitation **************************
export const joinAccountByInvitation = async (body: InvitationGetter) => {
  const res = await apiFetch<VerifiedUserResponse>(
    `/api/v1/account-invitations/join-account`,
    {
      method: 'POST',
      body,
    }
  );
  await saveUserSession(res);
  return res;
};
export const useJoinAccountByInvitation = () => {
  return useMutation({
    mutationKey: ['join-account-by-invitation'],
    mutationFn: joinAccountByInvitation,
  });
};
